import Vue from 'vue';
import mappable from '../models/mappable';
import Site from '../models/site';

export const initialState = {
  _idLookup: {},
  _activeFilters: 0,
  activeMarkers: 0,
  map: null,
  selectedMappable: null,
  loadingMoreMappables: true,
  mappables: [],
  activeMappables: [],
  inactiveMappables: [],
  filters: {},
  search: null,
  executeSearch: null,
  zones: [
    {
      name: 'All Zones',
      id: null,
      points: [],
    },
  ],
  settings: {
    displayFilterSettings: false,
    filters: {
      zones: {
        selectedZone: 'All Zones',
        selectedZoneId: null,
        selectedZonesAsOverlay: false,
      },
      siteTypes: {
        Trap: true,
        Larvicide: true,
        'Landing Rate': true,
        Adulticide: true,
        'Service Request': true,
        'No Spray': true,
        'Spray Reminder': true,
      },
      activity: {
        Scheduled: false,
        Inactive: true,
      },
      sortOrder: {
        by: 'Activity',
      },
    },
  },
}

export const mapStore = {
  state: () => (_.cloneDeep(initialState)),

  actions: {
    setDataRetrieved({ commit }, payload) {
      commit('dataRetrieved', payload);
    },
    selectMappable({ commit }, payload) {
      commit('setSelectedMappable', payload);
    },
    alterActiveMarkers({ commit }, payload) {
      commit('alterActiveMarkers', payload);
    },
    setMappableSearch({ commit }, payload) {
      commit('setSearch', payload);
    },
    applyFilterSettings({ commit }, payload) {
      commit('handleChangeFilters', payload);
    },
    setDisplayFilterSettings({ commit }, payload) {
      commit('setDisplayFilterSettings', payload);
    },
    reset({ commit }) {
      commit('reset')
    }
  },

  getters: {
    visibleMappables: (state) => {
      if (state._activeFilters === 0) {
        return state.mappables;
      }

      const checkPassesAllFilters = (mappable) =>
        Object.keys(state.filters).every((filterKey) => state.filters[filterKey](mappable));
      const filterer = (mappable) => checkPassesAllFilters(mappable);
      const visibleMappables = state.mappables.filter(filterer);
      return visibleMappables;
    },
    selectedZone: (state) => {
      const { selectedZoneId, selectedZonesAsOverlay } = state.settings.filters.zones;
      const zone = state.zones.find(({ id }) => id === selectedZoneId);
      const selectedZone = {
        ...zone,
        id: selectedZonesAsOverlay ? zone.id : null,
      };
      return selectedZone;
    },
  },

  mutations: {
    init(state) {
      state._idLookup = {};
      state.mappables = [];
      state.activeMappables = [];
      state.inactiveMappables = [];
      state.filters = {};
      state.selectedMappable = null;
      state.settings.filters.sortOrder.by = 'Activity';
    },

    reset(state) {
      Object.assign(state, _.cloneDeep(initialState))
    },

    setLoadingMoreMappables(state, val) {
      state.loadingMoreMappables = val;
    },

    resetMappables(state) {
      state._idLookup = {};
      state.mappables = [];
      state.activeMappables = [];
      state.inactiveMappables = [];
      state.selectedMappable = null;
    },

    setMapZones(state, zones) {
      state.zones = [
        {
          name: 'All Zones',
          id: null,
          points: [],
        },
        ...zones,
      ];
    },

    setSelectedMappable(state, newMappable) {
      const { selectedMappable } = state;
      state.selectedMappable = selectedMappable === newMappable ? null : newMappable;
    },

    updateSites(state, sites) {
      const newSites = sites.filter(({ id }) => {
        const siteAlreadyIn = !!state._idLookup[id];
        state._idLookup[id] = true;
        return !siteAlreadyIn;
      });
      state.activeMappables = [...state.activeMappables, ...newSites.filter((site) => site.isActive)];
      state.inactiveMappables = [...state.inactiveMappables, ...newSites.filter((site) => !site.isActive)];
    },

    alterActiveMarkers(state, amount) {
      state.activeMarkers += amount;
    },

    setSearch(state, { search, delay }) {
      const searchLowered = search.toLowerCase();
      const newSearch = searchLowered || null;
      if (state.executeSearch) {
        clearTimeout(state.executeSearch);
        state.executeSearch = null;
      }

      if (!newSearch) {
        this.commit('removeFilter', 'search');
      } else {
        state.selectedMappable = null;
        state.executeSearch = setTimeout(() => {
          const searchFn = ({ nameLowered, firstName, lastName }) =>
            nameLowered.includes(searchLowered) ||
            (firstName || '').toLowerCase().includes(searchLowered) ||
            (lastName || '').toLowerCase().includes(searchLowered);
          state.executeSearch = null;
          this.commit('applyFilter', {
            key: 'search',
            fn: searchFn,
          });
        }, delay || 0);
      }

      state.search = newSearch;
    },
    setDisplayFilterSettings(state, val) {
      state.settings.displayFilterSettings = val;
    },
    applyFilter(state, { key, fn }) {
      if (!state.filters[key]) {
        state._activeFilters += 1;
      }
      Vue.set(state.filters, key, fn);
    },
    removeFilter(state, key) {
      if (!state.filters[key]) {
        return;
      }
      state._activeFilters -= 1;
      Vue.delete(state.filters, key);
    },

    handleChangeFilters(state, newSettings) {
      this.commit('handleChangeZones', newSettings.zones);
      this.commit('handleChangeFilterSiteTypes', newSettings.siteTypes);
      this.commit('handleChangeActivity', newSettings.activity);
      this.commit('handleChangeSortOrder', newSettings.sortOrder);
    },

    handleChangeZones(state, zonesSettings) {
      const { settings } = state;
      const selectedZone = state.zones.find(({ name }) => name === zonesSettings.selectedZone);
      const selectedZoneId = selectedZone.id;
      settings.filters = {
        ...settings.filters,
        zones: {
          ...settings.filters.zones,
          ...zonesSettings,
          selectedZoneId,
        },
      };

      const filterId = 'filterByZone';
      if (!selectedZoneId) {
        this.commit('removeFilter', filterId);
      } else {
        this.commit('applyFilter', {
          key: filterId,
          fn: (mappable) => mappable.zoneId === selectedZoneId,
        });
      }
    },

    handleChangeFilterSiteTypes(state, filterSiteTypesSettings) {
      state.settings.filters = {
        ...state.settings.filters,
        siteTypes: filterSiteTypesSettings,
      };
      const filterId = 'siteTypes';
      const siteTypeKeys = Object.keys(filterSiteTypesSettings);
      const filteringNeeded = siteTypeKeys.some((key) => !filterSiteTypesSettings[key]);
      if (!filteringNeeded) {
        this.commit('removeFilter', filterId);
        return;
      }
      const buildSiteTypesFilter = (enabledSiteTypes) => (mappable) => mappable.hasAnyType(enabledSiteTypes);

      const enabledSiteTypes = siteTypeKeys.reduce(
        (acc, key) => (filterSiteTypesSettings[key] ? [...acc, key] : acc),
        []
      );

      this.commit('applyFilter', {
        key: filterId,
        fn: buildSiteTypesFilter(enabledSiteTypes),
      });
    },

    handleChangeActivity(state, activitySettings) {
      const { settings } = state;
      settings.filters = {
        ...settings.filters,
        activity: {
          ...settings.filters.activity,
          ...activitySettings,
        },
      };
      state.mappables = activitySettings.Inactive
        ? [...state.inactiveMappables, ...state.activeMappables]
        : state.activeMappables;

      const scheduledFilterId = 'isScheduled';
      if (activitySettings.Scheduled) {
        this.commit('applyFilter', {
          key: scheduledFilterId,
          fn: (mappable) => mappable.isScheduled,
        });
      } else {
        this.commit('removeFilter', scheduledFilterId);
      }
    },

    handleChangeSortOrder(state, sortOrderSettings) {
      state.settings.filters.sortOrder = {
        ...sortOrderSettings,
      };

      const sortedMappables = {
        Activity: sortActivity,
        Alphabetical: sortAlphabetical,
        Category: sortCategory,
        'Route Order': sortRouteOrder,
      }[sortOrderSettings.by]([...state.mappables]);

      state.mappables = sortedMappables;
    },
  },
};

const sortCompare = (a, b) => (a < b ? -1 : a > b ? 1 : 0);

const sortActivity = (mappables) => {
  mappables.sort((a, b) => {
    const activityA = a.getLatestActivityDate();
    const activityB = b.getLatestActivityDate();
    return sortCompare(activityB, activityA);
  });
  return mappables;
};

const sortAlphabetical = (mappables) => {
  mappables.sort(({ nameLowered: nameA }, { nameLowered: nameB }) => {
    const beginningNumbersAndOneSpace = /^\d+\s{1}/g;
    const regdNameA = nameA.replace(beginningNumbersAndOneSpace, '');
    const regdNameB = nameB.replace(beginningNumbersAndOneSpace, '');
    const regdResult = sortCompare(regdNameA, regdNameB);
    return regdResult === 0 ? sortCompare(nameA, nameB) : regdResult;
  });
  return mappables;
};

const sortCategory = (mappables) => {
  const reducer = (acc, mappable) => {
    const type = mappable.isSingleType ? mappable.type : 'Other';
    return {
      ...acc,
      [type]: [...acc[type], mappable],
    };
  };
  const categorizedMappables = mappables.reduce(reducer, {
    Adulticide: [],
    Larvicide: [],
    Trap: [],
    'Landing Rate': [],
    Other: [],
  });

  return Object.keys(categorizedMappables).reduce(
    (acc, key) => [...acc, ...categorizedMappables[key]],
    []
  );
};

const sortRouteOrder = (mappables) => {
  sortAlphabetical(mappables).sort((siteA, siteB) => {
    const hasLarvalA = siteA.hasLarvalSites;
    const hasLarvalB = siteB.hasLarvalSites;
    if (hasLarvalA && hasLarvalB) {
      const routeOrderA = siteA.larvalSites[0].routeOrder || 0;
      const routeOrderB = siteB.larvalSites[0].routeOrder || 0;
      return routeOrderA - routeOrderB;
    }
    return hasLarvalA ? -1 : hasLarvalB ? 1 : 0;
  });
  return mappables;
};
